// Pricing maths for the agent pricing dashboard. Pure and Angular-free on purpose: the services
// that consume it import CustomerdatabaseService, whose import cycle crashes the Karma bootstrap,
// so the unit tests target this module directly.
//
// Vocabulary, kept identical to MONA_Token_Model_v2 so the workbook and the app reconcile:
//   execution = one organigram request handled by an agent node
//   loop      = one LLM call made while serving an execution (measured avg 4.88 per execution)

import { MANAGED_WORKFLOWS } from './managed-workflows';

export type ModelTier = 'light' | 'standard' | 'advanced' | 'frontier' | 'voice';

export interface ModelRate {
  id: string;
  label: string;
  tier: ModelTier;
  costInPerM: number;
  costCachedPerM: number;
  costOutPerM: number;
  sellMultiple: number;
  priceIndex: number;
  baseFeePerYear: number;
  enabled: boolean;
}

export interface RecruitingRates {
  cvFilterPerApplication: number;
  pricePerInterview: number;
  interviewPriceMin?: number;
  interviewPriceMax?: number;
  applicationsPerYear: number;
  interviewsPerYear: number;
}

export function applicantCreatedMs(value: any): number {
  if (!value) { return 0; }
  if (typeof value.toDate === 'function') {
    const date = value.toDate();
    return date instanceof Date && !isNaN(date.getTime()) ? date.getTime() : 0;
  }
  if (typeof value.seconds === 'number') { return value.seconds * 1000; }
  if (typeof value._seconds === 'number') { return value._seconds * 1000; }
  if (typeof value === 'number') { return value > 1e12 ? value : value * 1000; }
  if (typeof value === 'string') {
    const date = new Date(value);
    return isNaN(date.getTime()) ? 0 : date.getTime();
  }
  return 0;
}

export interface ApplicantSignals {
  created?: any;
  logs?: any[] | null;
  answers?: any;
  readyForExport?: boolean;
  fallbackExport?: boolean;
  files?: any;
  cvFilterResult?: any;
  sentEmail?: any;
  sentSMS?: any;
  sentPhoneCall?: any;
  sentPhonecall?: any;
  sentEmailFourHours?: any;
  sentSMSFourHours?: any;
  sentPhoneFourHours?: any;
  sentEmailTwentyFourHours?: any;
  sentSMSTwentyFourHours?: any;
  sentPhoneTwentyFourHours?: any;
}

export interface RecruitingFunnel {
  applicants: number;
  withCv: number;
  cvFilterResults: number;
  interviewStarted: number;
  interviewCompleted: number;
  firstContact: number;
  secondContact: number;
  thirdContact: number;
  windowDays: number;
  from: string;
  to: string;
  measured: boolean;
  partial?: boolean;
  error?: string;
}

export interface RecruitingCharge {
  cvFilter: number;
  interviews: number;
  total: number;
  interviewsAtMin: number;
  interviewsAtMax: number;
}

export interface PricingConfig {
  currency: string;
  currencySymbol: string;
  models: ModelRate[];
  pricePerExecution: number;
  pricePerLoop: number;
  readinessFeePerIdleAgent: number;
  committedFloorPerYear: number;
  platformLayerPerYear: number;
  agentsLayerTargetPerYear: number;
  recruitingLayerTargetPerYear: number;
  recruiting: RecruitingRates;
  rolloutMultiple: number;
  agenticScopeMultiple: number;
  branchCoverage: number;
  // Used when the usage source records executions but not the LLM calls inside them. Default is
  // the value measured on real executor data (36,168 loops over 7,417 executions).
  assumedLoopsPerExecution: number;
  usagePanel: UsagePanelSettings;
  creditRecipe: CreditRecipe;
  updatedAt?: string;
  updatedBy?: string;
}

// What the customer-facing Usage & Credits panel shows. Independent of the internal quote:
// credits are the sell-side unit, and the pack price is what a credit is worth on that panel.
export interface UsagePanelSettings {
  // 'recipe' meters each run with the CreditRecipe; 'flat' charges creditsPerExecution per run.
  creditMode: 'recipe' | 'flat';
  creditsPerExecution: number;
  packPrice: number;
  packSize: number;
  currencySymbol: string;
  showCreditValue: boolean;
  customerSeesCost: boolean;
  showTokens: boolean;
  // 'YYYY-MM-DD' (UTC). Usage before it, such as setup and test runs, is never counted. '' = none.
  billingStart: string;
  // Credits included per calendar month; unused credits roll over. 0 = no allowance shown.
  monthlyCredits: number;
  // One-off pool, drawn only once the monthly credits and everything rolled over are used up.
  bonusCredits: number;
  candidatePackage: boolean;
}

export type ActionKind = 'email' | 'sms' | 'phone';

export interface ActionCounts {
  email: number;
  sms: number;
  phone: number;
}

export type ManagedUnits = Record<string, number>;

// The sell-side credit formula. Customers see credits only; the weights are never shown as a
// price per token. Token credits are per 1,000 tokens and multiplied by the model tier.
export interface CreditRecipe {
  runFee: number;
  perModelCall: number;
  inputPer1k: number;
  cachedPer1k: number;
  outputPer1k: number;
  defaultTier: number;
  modelTiers: Record<string, number>;
  actionCredits: ActionCounts;
  // What one action costs MONA, in EUR, as billed by the messaging and telephony providers.
  actionCostEur: ActionCounts;
  managedCredits: Record<string, number>;
  managedCostEur: Record<string, number>;
  // Upper bound on the model part of one run, so a looping agent cannot produce a runaway bill.
  capPerRun: number;
  minMargin: number;
}

export interface ModelRunUsage {
  calls: number;
  uncachedInput: number;
  cachedInput: number;
  output: number;
  reasoning: number;
}

// One execution, split by model, as recorded by the executor.
export interface RunUsage {
  models: Record<string, ModelRunUsage>;
}

export interface ExecutorTokens {
  input: number;
  cached: number;
  output: number;
  reasoning: number;
}

// The executor's input_tokens already contain cached_tokens and its output_tokens already contain
// reasoning_tokens (its total_tokens adds them again). Vertex token metrics match this reading, so
// input is the full prompt, cached is part of it, and output is the visible part only.
export function executorTokens(raw: any): ExecutorTokens {
  const count = (v: unknown) => Math.max(0, Number(v) || 0);
  const input = count(raw?.input_tokens);
  const output = count(raw?.output_tokens);
  const reasoning = Math.min(count(raw?.reasoning_tokens), output);
  return { input, cached: Math.min(count(raw?.cached_tokens), input), output: output - reasoning, reasoning };
}

export interface AgentUsageRow {
  nodeId: string;
  agentName: string;
  // The business process the run belongs to, taken from the parent trigger node. Executions are
  // recorded against the sending agent, so without this every row reads 'BA_email-sender'.
  processName?: string;
  organigramId: string;
  organigramName: string;
  nodeType: string;
  modelId: string;
  executions: number;
  loops: number;
  inputTokens: number;
  outputTokens: number;
  cachedTokens: number;
  reasoningTokens: number;
  idle: boolean;
  // False when the source records executions but not the LLM calls inside them (chat history has
  // no per-call breakdown), in which case the assumed loop multiplier is used instead.
  loopsKnown?: boolean;
  // Per-row provenance, so an exact row and an estimated row are never mistaken for each other
  // once both populations are shown side by side.
  rowSource?: 'executor' | 'chat-estimated';
  // Per-run breakdown for executor rows. Credits are metered per run because the cap is per run.
  runs?: RunUsage[];
}

export interface MonthlyUsage {
  runs: { month: string; run: RunUsage }[];
  // Chat-estimated usage, one synthetic row per agent and month.
  chats: { month: string; row: AgentUsageRow }[];
  actions: Record<string, ActionCounts>;
  managed?: Record<string, ManagedUnits>;
}

// Which collections the figures came from. 'executor' is exact; 'chat-estimated' derives tokens
// from message text; 'mixed' means both populations are present and each row is labelled.
export type UsageSource = 'executor' | 'chat-estimated' | 'mixed' | 'none';

export interface LoopPurposeRow {
  purpose: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  cachedTokens: number;
}

export interface ModelUsageRow {
  modelId: string;
  sessions: number;
  loops: number;
  inputTokens: number;
  outputTokens: number;
  cachedTokens: number;
}

export interface UsageWindow {
  rows: AgentUsageRow[];
  byPurpose: LoopPurposeRow[];
  byModel: ModelUsageRow[];
  windowDays: number;
  from: string;
  to: string;
  totalExecutions: number;
  totalLoops: number;
  source: UsageSource;
  // Emails, SMS and phone calls sent by agents in the window. Logged without a session id, so
  // they are counted per account rather than per process.
  actions?: ActionCounts;
  managed?: ManagedUnits;
  managedActions?: Record<string, ActionCounts>;
  // The billing start applied to this window, '' when none.
  billingStart?: string;
  // Everything since the billing start bucketed by calendar month, whatever timeframe is shown.
  monthly?: MonthlyUsage;
  daily?: DailyUsage;
  diagnostics?: {
    connected: boolean;
    accountId: string;
    userId: string;
    usageDocsFound: number;
    usageDocsInWindow: number;
    organigramsFound: number;
    nodesFound: number;
    agentNodesFound: number;
    unmappedNodeIds: number;
    chatAgentsFound: number;
    chatDocsFound: number;
    errors: string[];
  };
}

export interface AgentProjection {
  nodeId: string;
  agentName: string;
  processName?: string;
  rowSource?: 'executor' | 'chat-estimated';
  organigramName: string;
  nodeType: string;
  modelId: string;
  idle: boolean;
  execPilotPerYear: number;
  execFullPerYear: number;
  loopsPerExecution: number;
  loopsPerYear: number;
  inputMTokens: number;
  cachedMTokens: number;
  outputMTokens: number;
  tokenCost: number;
  pricePerExecution: number;
  pricePerLoop: number;
  revenue: number;
  margin: number;
  marginPct: number;
}

export interface ProjectionTotals {
  execPilotPerYear: number;
  execFullPerYear: number;
  loopsPerYear: number;
  inputMTokens: number;
  cachedMTokens: number;
  outputMTokens: number;
  tokenCost: number;
  executionRevenue: number;
  loopRevenue: number;
  revenue: number;
  margin: number;
  marginPct: number;
  idleAgents: number;
  activeAgents: number;
}

export interface RevenueBuild {
  modelBaseFees: number;
  executionRevenue: number;
  loopRevenue: number;
  readinessRevenue: number;
  agenticSubtotal: number;
  recruitingRevenue: number;
  recruitingCvFilter: number;
  recruitingInterviews: number;
  recruitingAtMin: number;
  recruitingAtMax: number;
  platformRevenue: number;
  contractedTotal: number;
  floor: number;
  invoiced: number;
  tokenCost: number;
  margin: number;
  marginPct: number;
  agenticVsTarget: number;
  recruitingVsTarget: number;
  totalVsFloor: number;
}

export interface Scenario {
  key: string;
  label: string;
  description: string;
  consumption: number;
  invoiced: number;
  heldByFloor: number;
}

export interface OutcomeUnit {
  key: string;
  label: string;
  rate: number;
  basis: string;
}

export interface BusinessVolumes {
  billedHoursPerYear: number;
  newBilledWorkersPerYear: number;
  shiftsPerYear: number;
  activeWorkers: number;
  applicationsPerYear: number;
}

export const DEFAULT_BUSINESS_VOLUMES: BusinessVolumes = {
  billedHoursPerYear: 9040000,
  newBilledWorkersPerYear: 6745,
  shiftsPerYear: 1130000,
  activeWorkers: 7961.54,
  applicationsPerYear: 180366
};

// Verified against https://ai.google.dev/gemini-api/docs/pricing on 2026-09-28 (Standard, paid
// tier) and converted from USD at DEFAULT_PRICING_CONFIG.currency's FX rate below. Still editable,
// because list prices move and a negotiated rate differs.
//
// Two rate-card traps worth keeping in view:
//   - Output is 8.3x the input rate on the Flash-Lite line, so output volume drives the bill.
//   - The Gemini 3.x Flash rates are introductory and DOUBLE on 1 January 2027.
const USD_TO_GBP = 0.79;
const usd = (rate: number) => Math.round(rate * USD_TO_GBP * 10000) / 10000;

export const DEFAULT_MODEL_RATES: ModelRate[] = [
  { id: 'gemini-3.5-flash-lite', label: 'Gemini 3.5 Flash Lite', tier: 'light', costInPerM: usd(0.30), costCachedPerM: usd(0.03), costOutPerM: usd(2.50), sellMultiple: 12, priceIndex: 1, baseFeePerYear: 0, enabled: true },
  { id: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash', tier: 'standard', costInPerM: usd(0.30), costCachedPerM: usd(0.03), costOutPerM: usd(2.50), sellMultiple: 8, priceIndex: 2.5, baseFeePerYear: 6000, enabled: true },
  // Introductory: $0.75 / $0.075 / $3.75 through 31 Dec 2026, doubling to $1.50 / $0.15 / $7.50.
  { id: 'gemini-3.6-flash', label: 'Gemini 3.6 Flash', tier: 'standard', costInPerM: usd(0.75), costCachedPerM: usd(0.075), costOutPerM: usd(3.75), sellMultiple: 8, priceIndex: 3, baseFeePerYear: 9000, enabled: true },
  // Pro is tiered by prompt size: these are the <= 200k rates ($2.50 / $0.25 / $15.00 above it).
  { id: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro', tier: 'advanced', costInPerM: usd(1.25), costCachedPerM: usd(0.125), costOutPerM: usd(10.00), sellMultiple: 6, priceIndex: 6, baseFeePerYear: 24000, enabled: true },
  { id: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash', tier: 'advanced', costInPerM: usd(1.50), costCachedPerM: usd(0.15), costOutPerM: usd(9.00), sellMultiple: 6, priceIndex: 6, baseFeePerYear: 24000, enabled: false },
  { id: 'gemini-3.8-flash', label: 'Gemini 3.8 Flash', tier: 'standard', costInPerM: usd(0.75), costCachedPerM: usd(0.075), costOutPerM: usd(3.75), sellMultiple: 8, priceIndex: 3, baseFeePerYear: 9000, enabled: false },
  { id: 'frontier', label: 'Frontier (GPT / Claude class)', tier: 'frontier', costInPerM: usd(3.00), costCachedPerM: usd(0.30), costOutPerM: usd(15.00), sellMultiple: 5, priceIndex: 12, baseFeePerYear: 60000, enabled: false },
  { id: 'voice-realtime', label: 'Voice / realtime', tier: 'voice', costInPerM: usd(3.50), costCachedPerM: usd(0.35), costOutPerM: usd(21.00), sellMultiple: 5, priceIndex: 10, baseFeePerYear: 30000, enabled: false }
];

export const DEFAULT_PRICING_CONFIG: PricingConfig = {
  currency: 'GBP',
  currencySymbol: '£',
  models: DEFAULT_MODEL_RATES.map(m => ({ ...m })),
  pricePerExecution: 0.1,
  pricePerLoop: 0.01,
  readinessFeePerIdleAgent: 1800,
  committedFloorPerYear: 690000,
  platformLayerPerYear: 120000,
  agentsLayerTargetPerYear: 360000,
  recruitingLayerTargetPerYear: 210000,
  recruiting: {
    cvFilterPerApplication: 0.2,
    pricePerInterview: 3,
    interviewPriceMin: 2,
    interviewPriceMax: 6,
    applicationsPerYear: 35223,
    interviewsPerYear: 15629
  },
  rolloutMultiple: 100 / 33,
  agenticScopeMultiple: 4.92,
  branchCoverage: 0.33,
  assumedLoopsPerExecution: 4.88,
  usagePanel: {
    creditMode: 'recipe',
    creditsPerExecution: 500,
    packPrice: 20,
    packSize: 50000,
    currencySymbol: '€',
    showCreditValue: true,
    customerSeesCost: false,
    showTokens: false,
    billingStart: '',
    monthlyCredits: 0,
    bonusCredits: 0,
    candidatePackage: false
  },
  // Calibrated on Blue Arrow's executor data (13,830 runs, 14 days to 2026-09-30): 1,250 credits
  // per average run. Every line keeps at least 70% margin at 20 per 50k credits.
  creditRecipe: {
    runFee: 100,
    perModelCall: 30,
    inputPer1k: 10,
    cachedPer1k: 1,
    outputPer1k: 80,
    defaultTier: 1,
    modelTiers: {
      'gemini-3.5-flash-lite': 1,
      'gemini-2.5-flash': 1,
      'gemini-3.6-flash': 2,
      'gemini-3.8-flash': 2,
      'gemini-2.5-pro': 5,
      'gemini-3.5-flash': 5,
      'frontier': 10,
      'voice-realtime': 10
    },
    actionCredits: { email: 100, sms: 1250, phone: 750 },
    actionCostEur: { email: 0, sms: 0.09, phone: 0.04 },
    managedCredits: { 'shift-fill': 50 },
    managedCostEur: { 'shift-fill': 0.0003 },
    capPerRun: 10000,
    minMargin: 0.7
  }
};

export const DEFAULT_USAGE_PANEL: UsagePanelSettings = { ...DEFAULT_PRICING_CONFIG.usagePanel };

export const DEFAULT_CREDIT_RECIPE: CreditRecipe = {
  ...DEFAULT_PRICING_CONFIG.creditRecipe,
  modelTiers: { ...DEFAULT_PRICING_CONFIG.creditRecipe.modelTiers },
  actionCredits: { ...DEFAULT_PRICING_CONFIG.creditRecipe.actionCredits },
  actionCostEur: { ...DEFAULT_PRICING_CONFIG.creditRecipe.actionCostEur },
  managedCredits: { ...DEFAULT_PRICING_CONFIG.creditRecipe.managedCredits },
  managedCostEur: { ...DEFAULT_PRICING_CONFIG.creditRecipe.managedCostEur }
};

export const ACTION_KINDS: ActionKind[] = ['email', 'sms', 'phone'];

export function emptyActionCounts(): ActionCounts {
  return { email: 0, sms: 0, phone: 0 };
}

// A communication_logs entry is billable when it reached the provider. Explicit failures are not.
export function actionKindOf(log: any): ActionKind | null {
  const kind = String(log?.communication_type || '').toLowerCase();
  if (kind !== 'email' && kind !== 'sms' && kind !== 'phone') { return null; }
  const response = log?.response;
  if (response && typeof response === 'object') {
    if (response.error) { return null; }
    if (/fail|error|undeliver|reject/i.test(String(response.status || ''))) { return null; }
  }
  return kind;
}

const nonNegative = (value: any, fallback: number): number => {
  const n = Number(value);
  return isFinite(n) && n >= 0 ? n : fallback;
};

export function creditRecipeOf(config: Partial<PricingConfig> | null | undefined): CreditRecipe {
  const stored: Partial<CreditRecipe> = config?.creditRecipe || {};
  const d = DEFAULT_CREDIT_RECIPE;
  const counts = (value: any, fallback: ActionCounts): ActionCounts => ({
    email: nonNegative(value?.email, fallback.email),
    sms: nonNegative(value?.sms, fallback.sms),
    phone: nonNegative(value?.phone, fallback.phone)
  });
  const perUnit = (value: any, fallback: Record<string, number>): Record<string, number> => {
    const out = { ...fallback };
    Object.entries(value || {}).forEach(([id, n]) => {
      const v = Number(n);
      if (id && isFinite(v) && v >= 0) { out[id] = v; }
    });
    return out;
  };
  const modelTiers: Record<string, number> = { ...d.modelTiers };
  Object.entries(stored.modelTiers || {}).forEach(([id, tier]) => {
    const n = Number(tier);
    if (id && isFinite(n) && n > 0) { modelTiers[id] = n; }
  });
  const cap = Number(stored.capPerRun);
  const minMargin = Number(stored.minMargin);
  const defaultTier = Number(stored.defaultTier);
  return {
    runFee: nonNegative(stored.runFee, d.runFee),
    perModelCall: nonNegative(stored.perModelCall, d.perModelCall),
    inputPer1k: nonNegative(stored.inputPer1k, d.inputPer1k),
    cachedPer1k: nonNegative(stored.cachedPer1k, d.cachedPer1k),
    outputPer1k: nonNegative(stored.outputPer1k, d.outputPer1k),
    defaultTier: isFinite(defaultTier) && defaultTier > 0 ? defaultTier : d.defaultTier,
    modelTiers,
    actionCredits: counts(stored.actionCredits, d.actionCredits),
    actionCostEur: counts(stored.actionCostEur, d.actionCostEur),
    managedCredits: perUnit(stored.managedCredits, d.managedCredits),
    managedCostEur: perUnit(stored.managedCostEur, d.managedCostEur),
    capPerRun: isFinite(cap) && cap > 0 ? cap : d.capPerRun,
    minMargin: isFinite(minMargin) && minMargin >= 0 && minMargin < 1 ? minMargin : d.minMargin
  };
}

export function usagePanelOf(config: Partial<PricingConfig> | null | undefined): UsagePanelSettings {
  const stored = config?.usagePanel || {};
  const panel: UsagePanelSettings = { ...DEFAULT_USAGE_PANEL, ...stored };
  if (panel.creditMode !== 'flat') { panel.creditMode = 'recipe'; }
  if (!isFinite(panel.creditsPerExecution) || panel.creditsPerExecution < 0) {
    panel.creditsPerExecution = DEFAULT_USAGE_PANEL.creditsPerExecution;
  }
  if (!isFinite(panel.packPrice) || panel.packPrice < 0) {
    panel.packPrice = DEFAULT_USAGE_PANEL.packPrice;
  }
  if (!isFinite(panel.packSize) || panel.packSize <= 0) {
    panel.packSize = DEFAULT_USAGE_PANEL.packSize;
  }
  if (!panel.currencySymbol) { panel.currencySymbol = DEFAULT_USAGE_PANEL.currencySymbol; }
  panel.showCreditValue = panel.showCreditValue !== false;
  panel.customerSeesCost = panel.customerSeesCost === true;
  panel.showTokens = panel.showTokens === true;
  panel.billingStart = billingStartDate(panel.billingStart) ? panel.billingStart : '';
  panel.monthlyCredits = Math.round(nonNegative(panel.monthlyCredits, 0));
  panel.bonusCredits = Math.round(nonNegative(panel.bonusCredits, 0));
  panel.candidatePackage = panel.candidatePackage === true;
  return panel;
}

export function monthKey(date: Date): string {
  return date.toISOString().slice(0, 7);
}

export function monthRanges(from: Date, now: Date = new Date(), maxMonths = 120): { month: string; from: Date; to: Date | null }[] {
  const out: { month: string; from: Date; to: Date | null }[] = [];
  if (!from || isNaN(from.getTime()) || from.getTime() > now.getTime()) { return out; }
  let start = from;
  while (start.getTime() <= now.getTime()) {
    const next = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1));
    out.push({ month: monthKey(start), from: start, to: next.getTime() > now.getTime() ? null : next });
    start = next;
  }
  return out.slice(-maxMonths);
}

export function emptyMonthlyUsage(): MonthlyUsage {
  return { runs: [], chats: [], actions: {}, managed: {} };
}

export function creditsByMonth(
  monthly: MonthlyUsage | null | undefined, recipe: CreditRecipe, panel: UsagePanelSettings
): Record<string, number> {
  const out: Record<string, number> = {};
  const add = (month: string, credits: number) => { out[month] = (out[month] || 0) + credits; };
  (monthly?.runs || []).forEach(r =>
    add(r.month, panel.creditMode === 'flat' ? creditsForExecutions(1, panel) : runCredits(r.run, recipe)));
  (monthly?.chats || []).forEach(c => add(c.month, rowCredits(c.row, recipe, panel)));
  Object.entries(monthly?.actions || {}).forEach(([month, a]) => add(month, actionCreditsFor(a, recipe, panel)));
  Object.entries(monthly?.managed || {}).forEach(([month, u]) => add(month, managedCreditsFor(u, recipe, panel)));
  return out;
}

export interface AllowanceState {
  enabled: boolean;
  month: string;
  monthlyCredits: number;
  rolledIn: number;
  available: number;
  used: number;
  monthlyLeft: number;
  bonusCredits: number;
  bonusUsed: number;
  bonusLeft: number;
  usingBonus: boolean;
  exhausted: boolean;
  // Share of this month's available credits (monthly + rolled over) that is used, capped at 1.
  usedShare: number;
}

// Walks every calendar month from the billing start (or the first recorded month) to `now`:
// unused monthly credits roll over, and the bonus pool only pays for what exceeds them.
export function allowanceState(
  byMonth: Record<string, number>, panel: UsagePanelSettings, now: Date = new Date()
): AllowanceState {
  const month = monthKey(now);
  const monthlyCredits = panel?.monthlyCredits || 0;
  const bonusCredits = panel?.bonusCredits || 0;
  const start = billingStartDate(panel?.billingStart);
  const recorded = Object.keys(byMonth || {}).filter(m => /^\d{4}-\d{2}$/.test(m) && m <= month).sort();
  let cursor = start ? monthKey(start) : (recorded[0] || month);
  let carry = 0;
  let bonusLeft = bonusCredits;
  let state = { rolledIn: 0, available: monthlyCredits, used: 0, bonusDrawn: 0 };
  let guard = 0;
  while (cursor <= month && guard++ < 1200) {
    const used = Math.max(0, Number(byMonth?.[cursor]) || 0);
    const available = monthlyCredits + carry;
    const excess = Math.max(0, used - available);
    const bonusDrawn = Math.min(excess, bonusLeft);
    state = { rolledIn: carry, available, used, bonusDrawn };
    bonusLeft -= bonusDrawn;
    carry = Math.max(0, available - used);
    const [y, m] = cursor.split('-').map(Number);
    cursor = m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
  }
  const monthlyLeft = Math.max(0, state.available - state.used);
  return {
    enabled: monthlyCredits > 0 || bonusCredits > 0,
    month,
    monthlyCredits,
    rolledIn: state.rolledIn,
    available: state.available,
    used: state.used,
    monthlyLeft,
    bonusCredits,
    bonusUsed: bonusCredits - bonusLeft,
    bonusLeft,
    usingBonus: state.used > state.available && bonusCredits > 0,
    exhausted: state.used > state.available + state.bonusDrawn,
    usedShare: state.available > 0 ? Math.min(1, state.used / state.available) : (state.used > 0 ? 1 : 0)
  };
}

export function billingStartDate(value: any): Date | null {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) { return null; }
  const d = new Date(raw + 'T00:00:00Z');
  return isNaN(d.getTime()) || d.toISOString().slice(0, 10) !== raw ? null : d;
}

export interface UsageCutoff {
  from: Date | null;
  // Once a billing start is set, a record without a date cannot be shown to fall after it.
  requireDate: boolean;
}

export function usageCutoff(days: number, billingStart: any, now = Date.now()): UsageCutoff {
  const window = days > 0 ? new Date(now - days * 86400000) : null;
  const start = billingStartDate(billingStart);
  if (!start) { return { from: window, requireDate: false }; }
  return { from: window && window.getTime() > start.getTime() ? window : start, requireDate: true };
}

export function inUsageWindow(created: Date | null, cutoff: UsageCutoff): boolean {
  if (!created || isNaN(created.getTime())) { return !cutoff.requireDate; }
  return !cutoff.from || created.getTime() >= cutoff.from.getTime();
}

export function creditUnitPrice(panel: UsagePanelSettings): number {
  return panel.packSize > 0 ? panel.packPrice / panel.packSize : 0;
}

export function creditsForExecutions(executions: number, panel: UsagePanelSettings): number {
  const n = Number(executions);
  const each = Number(panel?.creditsPerExecution);
  if (!isFinite(n) || n <= 0 || !isFinite(each) || each <= 0) { return 0; }
  return Math.round(n) * each;
}

export function creditValue(credits: number, panel: UsagePanelSettings): number {
  const n = Number(credits);
  if (!isFinite(n) || n <= 0) { return 0; }
  return n * creditUnitPrice(panel);
}

export function usageRateLabel(panel: UsagePanelSettings): string {
  const size = panel.packSize >= 1000 && panel.packSize % 1000 === 0
    ? (panel.packSize / 1000) + 'k'
    : String(panel.packSize);
  const price = Number.isInteger(panel.packPrice) ? String(panel.packPrice) : String(panel.packPrice);
  return `at Pro rate (${panel.currencySymbol}${price} / ${size})`;
}

export const CREDITS_PER_MILLION = 1000000;

export function pricePerMillion(panel: UsagePanelSettings): number {
  return Math.round(creditUnitPrice(panel) * CREDITS_PER_MILLION * 100) / 100;
}

export function withPricePerMillion(panel: UsagePanelSettings, price: number): UsagePanelSettings {
  const n = Number(price);
  return { ...panel, packPrice: isFinite(n) && n > 0 ? n : 0, packSize: CREDITS_PER_MILLION };
}

export function perMillionLabel(panel: UsagePanelSettings): string {
  const price = pricePerMillion(panel);
  return `${panel.currencySymbol}${Number.isInteger(price) ? price : price.toFixed(2)} per 1M credits`;
}

export function costVisible(panel: UsagePanelSettings, monaStaff: boolean): boolean {
  return !!panel?.showCreditValue && (monaStaff || panel.customerSeesCost === true);
}

export interface DailyUsage {
  runs: { day: string; nodeId: string; run: RunUsage }[];
  chats: { day: string; row: AgentUsageRow }[];
  actions: Record<string, ActionCounts>;
  managed: Record<string, ManagedUnits>;
  managedActions: Record<string, Record<string, ActionCounts>>;
}

export interface DayUsageLine {
  key: string;
  name: string;
  credits: number;
}

export interface DayUsage {
  day: string;
  credits: number;
  lines: DayUsageLine[];
}

export function emptyDailyUsage(): DailyUsage {
  return { runs: [], chats: [], actions: {}, managed: {}, managedActions: {} };
}

export function dayKey(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function dayRanges(from: Date, now: Date = new Date(), maxDays = 92): { day: string; from: Date; to: Date | null }[] {
  const out: { day: string; from: Date; to: Date | null }[] = [];
  if (!from || isNaN(from.getTime()) || from.getTime() > now.getTime()) { return out; }
  let start = from;
  while (start.getTime() <= now.getTime()) {
    const next = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 1);
    out.push({ day: dayKey(start), from: start, to: next.getTime() > now.getTime() ? null : next });
    start = next;
  }
  return out.slice(-maxDays);
}

export function creditsByDay(
  daily: DailyUsage | null | undefined, recipe: CreditRecipe, panel: UsagePanelSettings, names: Record<string, string> = {}
): DayUsage[] {
  const days = new Map<string, Map<string, DayUsageLine>>();
  const add = (day: string, key: string, name: string, credits: number) => {
    if (!(credits > 0)) { return; }
    const lines = days.get(day) || new Map<string, DayUsageLine>();
    days.set(day, lines);
    const line = lines.get(key) || { key, name, credits: 0 };
    line.credits += credits;
    lines.set(key, line);
  };
  (daily?.runs || []).forEach(r => add(r.day, r.nodeId, names[r.nodeId] || r.nodeId,
    panel.creditMode === 'flat' ? creditsForExecutions(1, panel) : runCredits(r.run, recipe)));
  (daily?.chats || []).forEach(c => add(c.day, c.row.nodeId, names[c.row.nodeId] || c.row.processName || c.row.agentName,
    rowCredits(c.row, recipe, panel)));
  const dayList = new Set([...Object.keys(daily?.actions || {}), ...Object.keys(daily?.managed || {}), ...Object.keys(daily?.managedActions || {})]);
  dayList.forEach(day => {
    const generic = { ...emptyActionCounts(), ...(daily?.actions?.[day] || {}) };
    MANAGED_WORKFLOWS.filter(w => w.metering).forEach(w => {
      const a = daily?.managedActions?.[day]?.[w.id] || emptyActionCounts();
      ACTION_KINDS.forEach(k => { generic[k] = Math.max(0, generic[k] - nonNegative(a[k], 0)); });
      add(day, 'managed:' + w.id, w.name,
        managedCreditsFor({ [w.id]: nonNegative(daily?.managed?.[day]?.[w.id], 0) }, recipe, panel) + actionCreditsFor(a, recipe, panel));
    });
    add(day, 'messages', 'Emails, SMS & calls', actionCreditsFor(generic, recipe, panel));
  });
  return Array.from(days.entries())
    .map(([day, lines]) => {
      const sorted = Array.from(lines.values()).sort((a, b) => b.credits - a.credits || a.name.localeCompare(b.name));
      return { day, credits: sorted.reduce((s, l) => s + l.credits, 0), lines: sorted };
    })
    .sort((a, b) => b.day.localeCompare(a.day));
}

export function tierFor(recipe: CreditRecipe, modelId: string): number {
  const tier = Number(recipe?.modelTiers?.[modelId]);
  return isFinite(tier) && tier > 0 ? tier : (recipe?.defaultTier || 1);
}

export function runCredits(run: RunUsage, recipe: CreditRecipe): number {
  let model = 0;
  Object.entries(run?.models || {}).forEach(([modelId, u]) => {
    const tier = tierFor(recipe, modelId);
    model += tier * (
      nonNegative(u?.calls, 0) * recipe.perModelCall
      + nonNegative(u?.uncachedInput, 0) / 1000 * recipe.inputPer1k
      + nonNegative(u?.cachedInput, 0) / 1000 * recipe.cachedPer1k
      + (nonNegative(u?.output, 0) + nonNegative(u?.reasoning, 0)) / 1000 * recipe.outputPer1k
    );
  });
  return Math.round(Math.min(recipe.capPerRun, recipe.runFee + model));
}

// Chat-estimated rows have no per-run records, so their average run is metered instead.
export function averageRunOf(row: AgentUsageRow): RunUsage {
  const exec = row.executions > 0 ? row.executions : 1;
  const calls = row.loopsKnown === false ? row.executions : row.loops;
  const cached = Math.min(row.cachedTokens || 0, row.inputTokens || 0);
  return {
    models: {
      [row.modelId || FALLBACK_MODEL_ID]: {
        calls: (calls || 0) / exec,
        uncachedInput: ((row.inputTokens || 0) - cached) / exec,
        cachedInput: cached / exec,
        output: (row.outputTokens || 0) / exec,
        reasoning: (row.reasoningTokens || 0) / exec
      }
    }
  };
}

export function rowCredits(row: AgentUsageRow, recipe: CreditRecipe, panel: UsagePanelSettings): number {
  if (!row || !(row.executions > 0)) { return 0; }
  if (panel?.creditMode === 'flat') { return creditsForExecutions(row.executions, panel); }
  if (row.runs?.length) { return row.runs.reduce((s, run) => s + runCredits(run, recipe), 0); }
  return runCredits(averageRunOf(row), recipe) * row.executions;
}

export function actionCreditsFor(actions: ActionCounts | null | undefined, recipe: CreditRecipe, panel: UsagePanelSettings): number {
  if (!actions || panel?.creditMode === 'flat') { return 0; }
  return ACTION_KINDS.reduce((s, k) => s + nonNegative(actions[k], 0) * recipe.actionCredits[k], 0);
}

export function managedCreditsFor(units: ManagedUnits | null | undefined, recipe: CreditRecipe, panel: UsagePanelSettings): number {
  if (!units || panel?.creditMode === 'flat') { return 0; }
  return Object.entries(units).reduce((s, [id, n]) => s + nonNegative(n, 0) * nonNegative(recipe.managedCredits?.[id], 0), 0);
}

export interface ManagedUsageLine {
  id: string;
  name: string;
  unitPlural: string;
  units: number;
  credits: number;
  actions: ActionCounts;
  actionCredits: number;
}

export function managedUsageLines(
  units: ManagedUnits | null | undefined, recipe: CreditRecipe, panel: UsagePanelSettings,
  managedActions?: Record<string, ActionCounts> | null
): ManagedUsageLine[] {
  return MANAGED_WORKFLOWS
    .filter(w => w.metering)
    .map(w => {
      const n = nonNegative(units?.[w.id], 0);
      const a = managedActions?.[w.id];
      const actions = { email: nonNegative(a?.email, 0), sms: nonNegative(a?.sms, 0), phone: nonNegative(a?.phone, 0) };
      return {
        id: w.id,
        name: w.name,
        unitPlural: w.metering!.unitPlural,
        units: n,
        credits: managedCreditsFor({ [w.id]: n }, recipe, panel),
        actions,
        actionCredits: actionCreditsFor(actions, recipe, panel)
      };
    })
    .filter(l => l.units > 0 || l.actions.email + l.actions.sms + l.actions.phone > 0);
}

export interface CreditTotals {
  runs: number;
  actions: number;
  managed: number;
  total: number;
}

export function usageCredits(
  rows: AgentUsageRow[], actions: ActionCounts | null | undefined, recipe: CreditRecipe, panel: UsagePanelSettings,
  managed?: ManagedUnits | null
): CreditTotals {
  const runs = (rows || []).reduce((s, r) => s + rowCredits(r, recipe, panel), 0);
  const actionCredits = actionCreditsFor(actions, recipe, panel);
  const managedCredits = managedCreditsFor(managed, recipe, panel);
  return { runs, actions: actionCredits, managed: managedCredits, total: runs + actionCredits + managedCredits };
}

// Rough FX to GBP, used only for the internal margin check. Model rates are held in the config
// currency, action costs in EUR, and the pack price in whatever the usage panel shows.
const GBP_PER_UNIT: Record<string, number> = { GBP: 1, '£': 1, EUR: 0.86, '€': 0.86, USD: 0.79, '$': 0.79 };

export function toGbp(currency: string): number {
  return GBP_PER_UNIT[String(currency || '').trim().toUpperCase()] || GBP_PER_UNIT[String(currency || '').trim()] || 1;
}

export function creditPriceInConfigCurrency(config: PricingConfig): number {
  const panel = usagePanelOf(config);
  return creditUnitPrice(panel) * toGbp(panel.currencySymbol) / toGbp(config.currency);
}

export function actionCostInConfigCurrency(kind: ActionKind, config: PricingConfig): number {
  return creditRecipeOf(config).actionCostEur[kind] * toGbp('EUR') / toGbp(config.currency);
}

export function managedCostInConfigCurrency(id: string, config: PricingConfig): number {
  return nonNegative(creditRecipeOf(config).managedCostEur[id], 0) * toGbp('EUR') / toGbp(config.currency);
}

export function runProviderCost(run: RunUsage, config: PricingConfig): number {
  return Object.entries(run?.models || {}).reduce((s, [modelId, u]) => {
    const m = findModel(config, modelId);
    return s + (
      nonNegative(u?.uncachedInput, 0) * m.costInPerM
      + nonNegative(u?.cachedInput, 0) * m.costCachedPerM
      + (nonNegative(u?.output, 0) + nonNegative(u?.reasoning, 0)) * m.costOutPerM
    ) / 1e6;
  }, 0);
}

export interface UsageMargin {
  credits: CreditTotals;
  revenue: number;
  tokenCost: number;
  actionCost: number;
  managedCost: number;
  cost: number;
  margin: number;
  marginPct: number;
}

export function usageMargin(
  rows: AgentUsageRow[], actions: ActionCounts | null | undefined, config: PricingConfig, managed?: ManagedUnits | null
): UsageMargin {
  const recipe = creditRecipeOf(config);
  const panel = usagePanelOf(config);
  const credits = usageCredits(rows, actions, recipe, panel, managed);
  const tokenCost = (rows || []).reduce((s, r) => {
    if (!(r.executions > 0)) { return s; }
    if (r.runs?.length) { return s + r.runs.reduce((c, run) => c + runProviderCost(run, config), 0); }
    return s + runProviderCost(averageRunOf(r), config) * r.executions;
  }, 0);
  const actionCost = ACTION_KINDS.reduce((s, k) => s + nonNegative(actions?.[k], 0) * actionCostInConfigCurrency(k, config), 0);
  const managedCost = Object.entries(managed || {})
    .reduce((s, [id, n]) => s + nonNegative(n, 0) * managedCostInConfigCurrency(id, config), 0);
  const revenue = credits.total * creditPriceInConfigCurrency(config);
  const cost = tokenCost + actionCost + managedCost;
  return {
    credits, revenue, tokenCost, actionCost, managedCost, cost,
    margin: revenue - cost,
    marginPct: revenue > 0 ? (revenue - cost) / revenue : 0
  };
}

export interface FloorCheckLine {
  key: string;
  label: string;
  unit: string;
  credits: number;
  price: number;
  cost: number;
  marginPct: number;
  floorCredits: number;
  ok: boolean;
}

// Each billable line priced on its own, so the minimum margin holds whatever the usage mix.
export function recipeFloorCheck(config: PricingConfig): FloorCheckLine[] {
  const recipe = creditRecipeOf(config);
  const unitPrice = creditPriceInConfigCurrency(config);
  const line = (key: string, label: string, unit: string, credits: number, cost: number): FloorCheckLine => {
    const price = credits * unitPrice;
    const floorCredits = unitPrice > 0 ? cost / (1 - recipe.minMargin) / unitPrice : Infinity;
    return {
      key, label, unit, credits, price, cost,
      marginPct: price > 0 ? (price - cost) / price : (cost > 0 ? -Infinity : 1),
      floorCredits,
      ok: credits >= floorCredits - 1e-9
    };
  };
  const lines: FloorCheckLine[] = ACTION_KINDS.map(k => line(
    k, k === 'sms' ? 'SMS' : k === 'phone' ? 'Phone call' : 'Email', 'each',
    recipe.actionCredits[k], actionCostInConfigCurrency(k, config)
  ));
  MANAGED_WORKFLOWS.filter(w => w.metering).forEach(w => lines.push(line(
    `managed:${w.id}`, w.name, w.metering!.unit,
    nonNegative(recipe.managedCredits[w.id], 0), managedCostInConfigCurrency(w.id, config)
  )));
  (config.models || []).filter(m => m.enabled).forEach(m => {
    const tier = tierFor(recipe, m.id);
    lines.push(line(`${m.id}:in`, `${m.label} input`, '1M tokens', tier * recipe.inputPer1k * 1000, m.costInPerM));
    lines.push(line(`${m.id}:cached`, `${m.label} cached`, '1M tokens', tier * recipe.cachedPer1k * 1000, m.costCachedPerM));
    lines.push(line(`${m.id}:out`, `${m.label} output`, '1M tokens', tier * recipe.outputPer1k * 1000, m.costOutPerM));
  });
  return lines;
}

export function creditFormulaLabel(panel: UsagePanelSettings): string {
  return panel?.creditMode === 'flat'
    ? `Credits = executions × ${panel.creditsPerExecution}`
    : 'Credits = runs, steps and messages sent';
}

const FALLBACK_MODEL_ID = 'gemini-3.5-flash-lite';

// Chat-history estimation, kept identical to AgentStatisticsService.aggregateMessageUsage so the
// pricing dashboard and the organigram Usage & Credits panel report the same interaction counts.
const CHARS_PER_TOKEN = 4;

export function estimateTokens(content: any): number {
  if (content === null || content === undefined) { return 0; }
  let text = '';
  if (typeof content === 'string') {
    text = content;
  } else {
    try { text = JSON.stringify(content); } catch { text = String(content); }
  }
  return text ? Math.ceil(text.length / CHARS_PER_TOKEN) : 0;
}

export function resolveMessageRole(msg: any): 'human' | 'assistant' | 'system' | 'unknown' {
  const raw = String(msg?.type || msg?.role || msg?.sender || '').toLowerCase();
  if (raw === 'human' || raw === 'user' || raw === 'customer') { return 'human'; }
  if (raw === 'ai' || raw === 'assistant' || raw === 'mona' || raw === 'agent' || raw === 'bot') { return 'assistant'; }
  if (raw === 'system') { return 'system'; }
  return 'unknown';
}

export function extractRecordedTokenUsage(msg: any): { input: number; output: number } | null {
  const raw = msg?.tokenUsage || msg?.token_usage || msg?.usage || null;
  if (!raw || typeof raw !== 'object') { return null; }
  const input = Number(raw.input ?? raw.input_tokens ?? raw.prompt_tokens ?? raw.promptTokens ?? 0) || 0;
  const output = Number(raw.output ?? raw.output_tokens ?? raw.completion_tokens ?? raw.completionTokens ?? 0) || 0;
  if (input <= 0 && output <= 0) { return null; }
  return { input, output };
}

export interface ChatAggregate {
  inputTokens: number;
  outputTokens: number;
  interactions: number;
  hasAccurateUsage: boolean;
}

export function aggregateChatMessages(messages: any[]): ChatAggregate {
  const list = Array.isArray(messages) ? messages : [];
  let inputTokens = 0;
  let outputTokens = 0;
  let interactions = 0;
  let hasAccurateUsage = false;

  list.forEach(msg => {
    const usage = extractRecordedTokenUsage(msg);
    if (!usage) { return; }
    hasAccurateUsage = true;
    inputTokens += usage.input;
    outputTokens += usage.output;
    interactions += 1;
  });
  if (hasAccurateUsage) {
    return { inputTokens, outputTokens, interactions, hasAccurateUsage };
  }

  let knownRoles = 0;
  let unknownIndex = 0;
  list.forEach(msg => {
    const role = resolveMessageRole(msg);
    if (role === 'system') { return; }
    const estimated = estimateTokens(msg?.content ?? msg?.text ?? msg?.message);
    if (!estimated) { return; }
    let effective = role;
    if (effective === 'unknown') {
      effective = unknownIndex % 2 === 0 ? 'human' : 'assistant';
      unknownIndex += 1;
    } else {
      knownRoles += 1;
    }
    if (effective === 'human') {
      inputTokens += estimated;
    } else {
      outputTokens += estimated;
      interactions += 1;
    }
  });
  if (interactions === 0 && (inputTokens > 0 || outputTokens > 0)) {
    interactions = Math.max(1, Math.ceil((knownRoles || list.length) / 2));
  }
  return { inputTokens, outputTokens, interactions, hasAccurateUsage: false };
}

// Guards against a collapsed measurement window. A window of one day multiplies the measured
// volume by 365 and silently inflates every downstream figure, so an implausibly short window is
// treated as unknown rather than scaled up.
const MIN_TRUSTWORTHY_WINDOW_DAYS = 2;

export function annualisationFactor(windowDays: number): number {
  if (!windowDays || windowDays <= 0) { return 1; }
  if (windowDays < MIN_TRUSTWORTHY_WINDOW_DAYS) { return 1; }
  return 365 / windowDays;
}

export function findModel(config: PricingConfig, modelId: string): ModelRate {
  const models = config?.models || [];
  return models.find(m => m.id === modelId)
    || models.find(m => m.id === FALLBACK_MODEL_ID)
    || models[0]
    || DEFAULT_MODEL_RATES[0];
}

export function tokenCostFor(model: ModelRate, inputMTokens: number, cachedMTokens: number, outputMTokens: number): number {
  const billableInput = Math.max(0, inputMTokens - cachedMTokens);
  return billableInput * model.costInPerM + cachedMTokens * model.costCachedPerM + outputMTokens * model.costOutPerM;
}

export function projectAgent(row: AgentUsageRow, config: PricingConfig, windowDays: number): AgentProjection {
  const model = findModel(config, row.modelId);
  const annualise = annualisationFactor(windowDays);
  const execPilotPerYear = row.executions * annualise;
  const execFullPerYear = execPilotPerYear * config.rolloutMultiple * config.agenticScopeMultiple;
  const loopsMeasured = row.loopsKnown !== false && row.executions > 0;
  const loopsPerExecution = loopsMeasured
    ? row.loops / row.executions
    : (row.executions > 0 ? (config.assumedLoopsPerExecution || 0) : 0);
  const loopsPerYear = execFullPerYear * loopsPerExecution;
  const inputPerExec = row.executions > 0 ? row.inputTokens / row.executions : 0;
  const outputPerExec = row.executions > 0 ? (row.outputTokens + (row.reasoningTokens || 0)) / row.executions : 0;
  const cacheRate = row.inputTokens > 0 ? row.cachedTokens / row.inputTokens : 0;
  const inputMTokens = execFullPerYear * inputPerExec / 1e6;
  const cachedMTokens = inputMTokens * cacheRate;
  const outputMTokens = execFullPerYear * outputPerExec / 1e6;
  const tokenCost = tokenCostFor(model, inputMTokens, cachedMTokens, outputMTokens);
  const pricePerExecution = config.pricePerExecution * model.priceIndex;
  const pricePerLoop = config.pricePerLoop * model.priceIndex;
  const revenue = execFullPerYear * pricePerExecution + loopsPerYear * pricePerLoop;
  const margin = revenue - tokenCost;
  return {
    nodeId: row.nodeId,
    agentName: row.agentName,
    processName: row.processName,
    rowSource: row.rowSource,
    organigramName: row.organigramName,
    nodeType: row.nodeType,
    modelId: model.id,
    idle: row.idle,
    execPilotPerYear,
    execFullPerYear,
    loopsPerExecution,
    loopsPerYear,
    inputMTokens,
    cachedMTokens,
    outputMTokens,
    tokenCost,
    pricePerExecution,
    pricePerLoop,
    revenue,
    margin,
    marginPct: revenue > 0 ? margin / revenue : 0
  };
}

export function projectAll(rows: AgentUsageRow[], config: PricingConfig, windowDays: number): {
  projections: AgentProjection[];
  totals: ProjectionTotals;
} {
  const projections = (rows || []).map(r => projectAgent(r, config, windowDays));
  const sum = (pick: (p: AgentProjection) => number) => projections.reduce((s, p) => s + pick(p), 0);
  const executionRevenue = sum(p => p.execFullPerYear * p.pricePerExecution);
  const loopRevenue = sum(p => p.loopsPerYear * p.pricePerLoop);
  const revenue = executionRevenue + loopRevenue;
  const tokenCost = sum(p => p.tokenCost);
  return {
    projections,
    totals: {
      execPilotPerYear: sum(p => p.execPilotPerYear),
      execFullPerYear: sum(p => p.execFullPerYear),
      loopsPerYear: sum(p => p.loopsPerYear),
      inputMTokens: sum(p => p.inputMTokens),
      cachedMTokens: sum(p => p.cachedMTokens),
      outputMTokens: sum(p => p.outputMTokens),
      tokenCost,
      executionRevenue,
      loopRevenue,
      revenue,
      margin: revenue - tokenCost,
      marginPct: revenue > 0 ? (revenue - tokenCost) / revenue : 0,
      idleAgents: projections.filter(p => p.idle).length,
      activeAgents: projections.filter(p => !p.idle).length
    }
  };
}

export function enabledBaseFees(config: PricingConfig): number {
  return (config?.models || []).filter(m => m.enabled).reduce((s, m) => s + (m.baseFeePerYear || 0), 0);
}

export function applicantHasCv(applicant: ApplicantSignals): boolean {
  if (applicant?.cvFilterResult) { return true; }
  const files = applicant?.files;
  if (Array.isArray(files)) { return files.length > 0; }
  return !!files;
}

export function classifyInterview(applicant: ApplicantSignals): { started: boolean; completed: boolean } {
  const logs = applicant?.logs;
  if (logs) {
    const list = Array.isArray(logs) ? logs : [];
    return {
      started: list.length > 0,
      completed: list.some(log => log && log.mona === 'Ende')
    };
  }
  if (applicant?.answers != undefined) { return { started: true, completed: true }; }
  if (applicant?.readyForExport && !applicant.fallbackExport) { return { started: true, completed: true }; }
  return { started: false, completed: false };
}

export function applicantInterviewStarted(applicant: ApplicantSignals): boolean {
  return classifyInterview(applicant).started;
}

export function applicantInterviewCompleted(applicant: ApplicantSignals): boolean {
  return classifyInterview(applicant).completed;
}

export const CONTACT_FIELDS = ['sentEmail', 'sentSMS', 'sentPhonecall', 'sentPhoneCall'];

export function applicantContacted(applicant: ApplicantSignals): boolean {
  return CONTACT_FIELDS.some(f => (applicant as any)?.[f] === true);
}

export function emptyRecruitingFunnel(): RecruitingFunnel {
  return {
    applicants: 0, withCv: 0, cvFilterResults: 0, interviewStarted: 0, interviewCompleted: 0,
    firstContact: 0, secondContact: 0, thirdContact: 0,
    windowDays: 0, from: '', to: '', measured: false
  };
}

export function tallyApplicants(applicants: ApplicantSignals[]): RecruitingFunnel {
  const funnel = emptyRecruitingFunnel();
  funnel.measured = true;
  (applicants || []).forEach(applicant => {
    funnel.applicants += 1;
    if (applicantHasCv(applicant)) { funnel.withCv += 1; }
    if (applicant?.cvFilterResult) { funnel.cvFilterResults += 1; }
    if (applicantInterviewStarted(applicant)) { funnel.interviewStarted += 1; }
    if (applicantInterviewCompleted(applicant)) { funnel.interviewCompleted += 1; }
    if (applicant?.sentEmail || applicant?.sentSMS || applicant?.sentPhoneCall) { funnel.firstContact += 1; }
    if (applicant?.sentEmailFourHours || applicant?.sentSMSFourHours || applicant?.sentPhoneFourHours) { funnel.secondContact += 1; }
    if (applicant?.sentEmailTwentyFourHours || applicant?.sentSMSTwentyFourHours || applicant?.sentPhoneTwentyFourHours) { funnel.thirdContact += 1; }
  });
  return funnel;
}

export function recruitingCharge(config: PricingConfig, coverage = 1): RecruitingCharge {
  const r = config.recruiting;
  const cvFilter = r.applicationsPerYear * r.cvFilterPerApplication * coverage;
  const interviews = r.interviewsPerYear * r.pricePerInterview * coverage;
  const min = r.interviewPriceMin ?? 2;
  const max = r.interviewPriceMax ?? 6;
  return {
    cvFilter,
    interviews,
    total: cvFilter + interviews,
    interviewsAtMin: r.interviewsPerYear * min * coverage,
    interviewsAtMax: r.interviewsPerYear * max * coverage
  };
}

export function recruitingRevenue(config: PricingConfig, coverage = 1): number {
  return recruitingCharge(config, coverage).total;
}

export function buildRevenue(totals: ProjectionTotals, config: PricingConfig): RevenueBuild {
  const modelBaseFees = enabledBaseFees(config);
  const readinessRevenue = totals.idleAgents * config.readinessFeePerIdleAgent;
  const agenticSubtotal = modelBaseFees + totals.executionRevenue + totals.loopRevenue + readinessRevenue;
  // Recruiting is quoted at the full footprint, so the measured per-branch rate is grossed up.
  const charge = recruitingCharge(config);
  const recruiting = charge.total * config.rolloutMultiple;
  const recruitingCvFilter = charge.cvFilter * config.rolloutMultiple;
  const recruitingInterviews = charge.interviews * config.rolloutMultiple;
  const recruitingAtMin = (charge.cvFilter + charge.interviewsAtMin) * config.rolloutMultiple;
  const recruitingAtMax = (charge.cvFilter + charge.interviewsAtMax) * config.rolloutMultiple;
  const contractedTotal = agenticSubtotal + recruiting + config.platformLayerPerYear;
  const invoiced = Math.max(contractedTotal, config.committedFloorPerYear);
  const margin = agenticSubtotal - totals.tokenCost;
  return {
    modelBaseFees,
    executionRevenue: totals.executionRevenue,
    loopRevenue: totals.loopRevenue,
    readinessRevenue,
    agenticSubtotal,
    recruitingRevenue: recruiting,
    recruitingCvFilter,
    recruitingInterviews,
    recruitingAtMin,
    recruitingAtMax,
    platformRevenue: config.platformLayerPerYear,
    contractedTotal,
    floor: config.committedFloorPerYear,
    invoiced,
    tokenCost: totals.tokenCost,
    margin,
    marginPct: agenticSubtotal > 0 ? margin / agenticSubtotal : 0,
    agenticVsTarget: config.agentsLayerTargetPerYear > 0 ? agenticSubtotal / config.agentsLayerTargetPerYear : 0,
    recruitingVsTarget: config.recruitingLayerTargetPerYear > 0 ? recruiting / config.recruitingLayerTargetPerYear : 0,
    totalVsFloor: config.committedFloorPerYear > 0 ? contractedTotal / config.committedFloorPerYear : 0
  };
}

export function buildScenarios(totals: ProjectionTotals, config: PricingConfig): Scenario[] {
  const build = buildRevenue(totals, config);
  const floor = config.committedFloorPerYear;
  const metered = totals.executionRevenue + totals.loopRevenue;
  const scaleBack = (config.rolloutMultiple * config.agenticScopeMultiple) || 1;
  const lightModel = (config.models || []).find(m => m.tier === 'light');

  const make = (key: string, label: string, description: string, consumption: number): Scenario => ({
    key, label, description,
    consumption,
    invoiced: Math.max(consumption, floor),
    heldByFloor: Math.max(0, floor - consumption)
  });

  return [
    make('full', 'Full rollout, all agents on', 'Design case', build.contractedTotal),
    make('pilot', 'Pilot today (measured run-rate)', 'Current branches, current agent scope',
      build.modelBaseFees + metered / scaleBack + build.readinessRevenue
      + recruitingRevenue(config) + config.platformLayerPerYear),
    make('agentsOff', 'Customer switches all agents off', 'Base fees, readiness, recruiting and platform still bill',
      build.modelBaseFees + build.readinessRevenue + build.recruitingRevenue + config.platformLayerPerYear),
    make('lightOnly', 'Customer drops to the light model only', 'Loses the model base fees',
      (lightModel?.enabled ? lightModel.baseFeePerYear : 0) + metered + build.readinessRevenue
      + build.recruitingRevenue + config.platformLayerPerYear)
  ];
}

export function outcomeUnits(invoiced: number, volumes: BusinessVolumes): OutcomeUnit[] {
  const safe = (n: number) => (n > 0 ? n : 1);
  return [
    { key: 'hour', label: 'Per billed hour', rate: invoiced / safe(volumes.billedHoursPerYear), basis: 'Invoiced / billed hours' },
    { key: 'worker', label: 'Per new billed worker', rate: invoiced / safe(volumes.newBilledWorkersPerYear), basis: 'Invoiced / new billed workers' },
    { key: 'shift', label: 'Per shift', rate: invoiced / safe(volumes.shiftsPerYear), basis: 'Invoiced / shifts' },
    { key: 'activeWorker', label: 'Per active worker / month', rate: invoiced / safe(volumes.activeWorkers) / 12, basis: 'Invoiced / active workers / 12' },
    { key: 'application', label: 'Per application', rate: invoiced / safe(volumes.applicationsPerYear), basis: 'Invoiced / applications' }
  ];
}

export type SimulationScope = 'account' | 'organigram' | 'agent' | 'session';

export interface SimulationInput {
  scope: SimulationScope;
  modelId: string;
  executions: number;
  loopsPerExecution: number;
  inputTokensPerLoop: number;
  outputTokensPerLoop: number;
  cacheHitRate: number;
}

export interface SimulationResult {
  modelId: string;
  modelLabel: string;
  executions: number;
  loops: number;
  inputTokens: number;
  cachedTokens: number;
  outputTokens: number;
  totalTokens: number;
  tokenCost: number;
  costPerExecution: number;
  pricePerExecution: number;
  pricePerLoop: number;
  executionRevenue: number;
  loopRevenue: number;
  revenue: number;
  revenuePerExecution: number;
  margin: number;
  marginPct: number;
}

export function simulate(input: SimulationInput, config: PricingConfig): SimulationResult {
  const model = findModel(config, input.modelId);
  const executions = Math.max(0, input.executions || 0);
  const loops = executions * Math.max(0, input.loopsPerExecution || 0);
  const inputTokens = loops * Math.max(0, input.inputTokensPerLoop || 0);
  const outputTokens = loops * Math.max(0, input.outputTokensPerLoop || 0);
  const cacheRate = Math.min(1, Math.max(0, input.cacheHitRate || 0));
  const cachedTokens = inputTokens * cacheRate;
  const tokenCost = tokenCostFor(model, inputTokens / 1e6, cachedTokens / 1e6, outputTokens / 1e6);
  const pricePerExecution = config.pricePerExecution * model.priceIndex;
  const pricePerLoop = config.pricePerLoop * model.priceIndex;
  const executionRevenue = executions * pricePerExecution;
  const loopRevenue = loops * pricePerLoop;
  const revenue = executionRevenue + loopRevenue;
  return {
    modelId: model.id,
    modelLabel: model.label,
    executions,
    loops,
    inputTokens,
    cachedTokens,
    outputTokens,
    totalTokens: inputTokens + outputTokens,
    tokenCost,
    costPerExecution: executions > 0 ? tokenCost / executions : 0,
    pricePerExecution,
    pricePerLoop,
    executionRevenue,
    loopRevenue,
    revenue,
    revenuePerExecution: executions > 0 ? revenue / executions : 0,
    margin: revenue - tokenCost,
    marginPct: revenue > 0 ? (revenue - tokenCost) / revenue : 0
  };
}

export function simulationFromAgent(row: AgentUsageRow, scope: SimulationScope): SimulationInput {
  const loopsPerExecution = row.executions > 0 ? row.loops / row.executions : 0;
  const loops = row.loops || 1;
  return {
    scope,
    modelId: row.modelId,
    executions: scope === 'session' ? 1 : row.executions,
    loopsPerExecution,
    inputTokensPerLoop: row.inputTokens / loops,
    outputTokensPerLoop: (row.outputTokens + (row.reasoningTokens || 0)) / loops,
    cacheHitRate: row.inputTokens > 0 ? row.cachedTokens / row.inputTokens : 0
  };
}

// EnhancedAuthGuard matches its no-database deny-list with url.includes(), so this path must not
// contain any of those fragments. '/admin/pricing' collided with '/pricing' and the page loaded
// without a tenant database connection, showing an empty rate card and no usage.
export const AGENT_PRICING_ROUTE = '/admin/agent-costs';

export const GUARD_NO_DATABASE_FRAGMENTS = [
  '/login', '/register', '/signup', '/pricing', '/account-confirmation',
  '/waitlist-confirm', '/emailverify', '/success'
];

const ADMIN_EMAIL_DOMAINS = ['@mona-ai.de', '@mona-ai.services'];

// Only MONA staff may edit rates or see provider cost. Kept here rather than in the service so it
// is covered by unit tests without pulling in the Firestore import cycle.
export function isMonaAdminEmail(email: unknown): boolean {
  if (typeof email !== 'string') { return false; }
  const normalised = email.trim().toLowerCase();
  return ADMIN_EMAIL_DOMAINS.some(domain => normalised.endsWith(domain));
}

export function canSeeCostAndMargin(isAdmin: boolean, viewMode: 'internal' | 'customer'): boolean {
  return isAdmin && viewMode === 'internal';
}

export function mergeConfig(stored: Partial<PricingConfig> | null | undefined): PricingConfig {
  const base: PricingConfig = {
    ...DEFAULT_PRICING_CONFIG,
    models: DEFAULT_MODEL_RATES.map(m => ({ ...m })),
    recruiting: { ...DEFAULT_PRICING_CONFIG.recruiting },
    usagePanel: { ...DEFAULT_USAGE_PANEL },
    creditRecipe: creditRecipeOf(null)
  };
  if (!stored) { return base; }
  const merged: PricingConfig = {
    ...base,
    ...stored,
    recruiting: { ...base.recruiting, ...(stored.recruiting || {}) },
    usagePanel: usagePanelOf(stored),
    creditRecipe: creditRecipeOf(stored),
    models: base.models.map(def => {
      const found = (stored.models || []).find(m => m && m.id === def.id);
      return found ? { ...def, ...found } : def;
    })
  };
  // Models discovered in live usage that are not in the defaults still need a rate, otherwise
  // their spend silently falls back to the light tier.
  (stored.models || []).forEach(m => {
    if (m && m.id && !merged.models.some(x => x.id === m.id)) {
      merged.models.push({ ...DEFAULT_MODEL_RATES[0], ...m });
    }
  });
  return merged;
}
